/**
 * @file hooks.ts
 * @description Хуки для удобной работы с AuthContext.
 *
 * Все хуки тонкие — это не логика, а удобный sugar поверх useContext, чтобы
 * не повторять `useContext(AuthContext)` в каждом компоненте и не забывать
 * проверять, что провайдер смонтирован.
 */

import { useContext } from "react";

import { AuthContext, type AuthContextValue } from "./AuthContext";
import type { AccountContext } from "./types";

/** Главный хук — кидает, если вы забыли обернуть в <AuthProvider>. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth(): компонент должен быть внутри <AuthProvider>");
  }
  return ctx;
}

/** Активный AccountContext (или null, если не выбран / не залогинен). */
export function useCurrentContext(): AccountContext | null {
  return useAuth().me?.activeContext ?? null;
}

/**
 * Есть ли у активного контекста заданный permission.
 * Возвращает false, если активного контекста нет.
 */
export function useHasPermission(permission: string): boolean {
  const ctx = useCurrentContext();
  return ctx?.role.permissions.includes(permission) ?? false;
}

/**
 * Есть ли у активного контекста заданная роль (по коду; опционально — внутри
 * конкретной категории).
 */
export function useHasRole(code: string, category?: "PLATFORM" | "SHOP"): boolean {
  const ctx = useCurrentContext();
  if (!ctx) return false;
  if (ctx.role.code !== code) return false;
  if (category && ctx.role.category !== category) return false;
  return true;
}
